import { useEffect, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { setAIAssistantDisplayed } from '@/aiAssistant/aiAssistant.actions';
import { getDefaultDisplayed } from '@/aiAssistant/aiAssistant.utilities';
import { subscribeToAuthChangeChannel } from '@/homescreen/homescreen.actions';
import { useLicenseManagementActions } from '@/licenseManagement/licenseManagement.store';
import { getLicenseStatusFromLicense, normalizeLicenseData } from '@/licenseManagement/licenseManagement.utilities';
import { LoadingFallback } from '@/main/LoadingFallback.atom';
import { StoreProviders } from '@/main/StoreProviders';
import { cleanupOnUnload } from '@/main/cleanup.utilities';
import { isEmbeddedView, setNavigate } from '@/main/routing.utilities';
import { resolveTelemetryConfig } from '@/main/telemetry.utilities';
import { cancelRequestsOnUnload } from '@/requests/pendingRequests.utilities';
import { cleanupBroadcastChannels } from '@/services/broadcastChannel.utilities';
import { addEventListeners } from '@/services/notifier.service';
import { fetchConfiguration } from '@/services/systemConfiguration.actions';
import { SystemMessageListener } from '@/systemWarning/SystemMessageListener';
import { Tracker } from '@/track/Tracker.atom';
import { isCsrfSet } from '@/utilities/auth.utilities';
import { init18n } from '@/utilities/i18n.utilities';
import { useApiGetLicenseQuery } from '@seeqdev/api/endpoint/SystemApi';
import { TelemetryProvider, useRouteTelemetry } from '@seeqdev/telemetry';

/**
 * Wrapper component that wraps all the Route components.
 * Everything that has to happen once per application load and applies to every single component should happen here.
 */
export const Main = () => {
  const navigate = useNavigate();
  const [configLoaded, setConfigLoaded] = useState(false);
  const [isI18nInitialized, setIsI18nInitialized] = useState(false);
  const { setLicense, setLicenseStatus } = useLicenseManagementActions();
  const { data: license, isLoading: isLoadingLicense } = useApiGetLicenseQuery({ enabled: isCsrfSet() });

  // Publish the current route onto telemetry metrics. No-op until telemetry is
  // initialized below (only when enabled), and safe to call before the loading
  // gate since hooks must run unconditionally.
  useRouteTelemetry();

  useEffect(() => {
    if (!license) {
      return;
    }

    // Keep the license store in sync with the app-root license query.
    setLicense(normalizeLicenseData(license));
    setLicenseStatus(getLicenseStatusFromLicense(license));
  }, [license, setLicense, setLicenseStatus]);

  useEffect(() => {
    fetchConfiguration().then(() => {
      init18n()
        .then(() => {
          setIsI18nInitialized(true);
        })
        .catch((error) => {
          console.error('Error initializing i18n: ', error);
        });

      setConfigLoaded(true);
      if (!isEmbeddedView()) {
        setAIAssistantDisplayed(getDefaultDisplayed(), true);
      }
    });
    subscribeToAuthChangeChannel();
    addEventListeners();

    // we need to expose the navigate function to call it from services
    setNavigate(navigate);
    const beforeUnloadListener = () => cancelRequestsOnUnload();
    window.addEventListener('beforeunload', beforeUnloadListener);
    return () => {
      cleanupBroadcastChannels();
      cancelRequestsOnUnload();
      cleanupOnUnload();
      window.removeEventListener('beforeunload', beforeUnloadListener);
    };
  }, []);

  if (!configLoaded || !isI18nInitialized || isLoadingLicense) {
    return <LoadingFallback />;
  }

  // Resolve telemetry config now that configuration has loaded; null when the
  // operator has not enabled telemetry.
  const telemetryConfig = resolveTelemetryConfig();

  return (
    <StoreProviders>
      <SystemMessageListener />
      <Tracker />
      {telemetryConfig ? (
        <TelemetryProvider config={telemetryConfig}>
          <Outlet />
        </TelemetryProvider>
      ) : (
        <Outlet />
      )}
    </StoreProviders>
  );
};
